import { type ReactNode, useMemo, useState } from "react";
import { type LineItem, PackageEntrySource } from "../models/CartData";
import CircleSpinner from "../../../../common/components/CircleSpinner/CircleSpinner";
import { useSelector } from "react-redux";

import { selectIsCartAboveThreshold } from "../redux/selectors/cartSelectors";
import { isConfiguratorProduct } from "~/domains/product/types/ProductData";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { buildPackagingOptions } from "~/domains/product/utils/buildPackagingOptions";
import { useTranslation } from "react-i18next";
import { useSalesData } from "~/features/salesCampaign/hooks/useSalesData";

import { translateWarehouseName } from "~/utils/language/translationUtils";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { IconButton, InputAdornment, styled, TextField } from "~/common/mui";
import EditIcon from "@mui/icons-material/Edit";

import { Parameter } from "~/common/components/ParameterV2";
import { SelectPackagingDialog } from "~/features/cart/cartV2/SelectPackagingDialog";
import { FormStaticText } from "~/common/components/FormStaticText/FormStaticText";

interface Props {
  product: ProductDescriptor;
  lineItem: LineItem;
  onChange: (packCode: string) => void;
}

export const EditablePackagingCell = (props: Props & { isPackagingSectionOpen?: boolean }) => {
  const { product, lineItem } = props;

  if (isConfiguratorProduct(product.base) || isProductPMA(product)) {
    return <StandardPackagingVariant />;
  }

  if (!product.base.quantityVisible) {
    return <DtsVariant />;
  }

  if (
    lineItem.source === PackageEntrySource.QUOTE ||
    lineItem.source === PackageEntrySource.CONTRACT ||
    lineItem.restrictedPackage
  ) {
    return <ViewablePackagingCell {...props} />;
  }

  return <DefaultVariant {...props} />;
};

export const ViewablePackagingCell = (props: Omit<Props, "onChange">) => {
  const { product } = props;

  if (!product.base.quantityVisible) {
    return <DtsVariant />;
  }

  return <DefaultViewableVariant {...props.lineItem} />;
};

const StandardPackagingVariant = () => {
  const { t } = useTranslation();

  return <Parameter label={t("addToCart.contract.packaging")} value={t("cartPage.standardPackaging")} />;
};

const DtsVariant = () => {
  const { t } = useTranslation();

  return <Parameter label={t("addToCart.contract.packaging")} value={t("product.dts")} />;
};

const DefaultViewableVariant = ({
  packSize,
  packCode,
  quantity,
  primaryWarehouseName,
}: Pick<LineItem, "packSize" | "packCode" | "quantity" | "primaryWarehouseName">) => {
  const { t } = useTranslation();
  if (!useSelector(selectIsCartAboveThreshold) && packSize === undefined) {
    return <CircleSpinner />;
  }

  let packagingLabel: ReactNode = "-";
  if (packSize !== undefined && packSize > 0) {
    packagingLabel = t("cartPage.table.header.quantity.value", {
      packSize: packSize,
      code: packCode,
      qty: Math.floor(quantity / packSize),
    });
  }

  return (
    <FormStaticText
      label={t("addToCart.contract.packaging")}
      value={packagingLabel}
      helperText={translateWarehouseName(t, primaryWarehouseName)}
    />
  );
};

const DefaultVariant = ({ lineItem, product, onChange }: Props & { isPackagingSectionOpen?: boolean }) => {
  const { t } = useTranslation();
  if (!useSelector(selectIsCartAboveThreshold) && lineItem.packSize === undefined) {
    return <CircleSpinner />;
  }

  return (
    <PackagingIndicator
      packCode={lineItem.packCode}
      packSize={lineItem.packSize ?? 1}
      quantity={lineItem.quantity}
      warehouseName={translateWarehouseName(t, lineItem.primaryWarehouseName)}
      className="packaging"
      onChange={onChange}
      product={product}
    />
  );
};

interface PackagingIndicatorProps {
  className?: string;
  packSize: number;
  packCode: string;
  quantity: number;
  onChange: (packCode: string) => void;
  warehouseName?: string;
  product: ProductDescriptor;
}

const PackagingIndicator = ({
  className,
  packCode,
  packSize,
  quantity,
  onChange,
  product,
  warehouseName,
}: PackagingIndicatorProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const salesData = useSalesData();
  const options = useMemo(
    () =>
      buildPackagingOptions(product.availability?.packagingInformation ?? [], product, salesData?.salePerPack ?? {}, t),
    [product, salesData?.salePerPack, t],
  );

  let label;
  let disabled;
  if (isConfiguratorProduct(product.base) || isProductPMA(product)) {
    label = "disabled!";
    disabled = true;
  } else {
    label = t("cartPage.table.header.quantity.value", {
      packSize: packSize,
      code: packCode,
      qty: Math.floor(quantity / packSize),
    });
    disabled = false;
  }

  let extraProps = {};
  if (!disabled) {
    extraProps = {
      sx: { pointerEvents: "none" },
      InputProps: {
        readOnly: true, // Prevents mobile keyboard
        endAdornment: (
          // pointerEvents: 'none' ensures the click bubbles up to the TextField
          // and avoids focus getting stuck on the icon itself
          <InputAdornment position="end" sx={{ pointerEvents: "auto" }}>
            <IconButton onClick={() => setOpen(true)}>
              <EditIcon />
            </IconButton>
          </InputAdornment>
        ),
        inputProps: {
          // Tells Screen Reader: "This is a button, not a text box"
          role: "button",
          // Tells Screen Reader: "Clicking this opens a dialog/menu"
          "aria-haspopup": "true",
          // Tells Screen Reader: "The popup is currently OPEN or CLOSED"
          // "aria-expanded": open,
          // (Optional) Explicit label if the visual label isn't enough
          "aria-label": "Select Item, Current value: Selected Option",
        },
      },
    };
  }

  return (
    <>
      <TextField
        fullWidth
        label="Choose packaging"
        className={className}
        value={label}
        helperText={warehouseName}
        disabled={disabled}
        {...extraProps}
      />
      <SelectPackagingDialog
        open={open}
        onClose={() => setOpen(false)}
        selectedPackCode={packCode}
        options={options}
        onSelectPackCode={onChange}
      />
    </>
  );
};

const FakeTextField = styled(TextField)({
  pointerEvents: "none",
  [`& .MuiInputBase-root`]: {
    paddingRight: "4px",
  },
  [`& .MuiInputAdornment-root`]: {
    pointerEvents: "all",
  },
});

interface PackagingCellInputProps {
  disabled?: boolean;
  value: string;
  helperText?: string;
  onClick: () => void;
  icon: ReactNode;
}

export const PackagingCellInput = ({ onClick, icon, ...rest }: PackagingCellInputProps) => {
  const { t } = useTranslation();
  return (
    <FakeTextField
      data-test-id={"btn-edit-packaging"}
      label={t(`cartPage.table.content.choosePackaging`)}
      {...rest}
      sx={{ paddingRight: 0 }}
      slotProps={{
        inputLabel: {
          shrink: true,
        },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onClick} disabled={rest.disabled} color={"primary"}>
                {icon}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
